import { Component, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { setTheme } from 'ngx-bootstrap/utils';
import { concatMap, filter } from 'rxjs/operators';
import { appInjector } from './shared/app-injector';
import { BaseComponent } from './shared/base-components/base.component';
import { Dictionary } from './shared/models/dictionary.model';
import { SecurityTransactionMenuItem } from './shared/models/security-transaction-menu-item/security-transaction-menu-item.model';
import { BusinessTransactionService } from './shared/services/business-transaction/business-transaction.service';
import { RedirectTransactionService } from './shared/services/business-transaction/redirect-transaction.service';
import { ActionTopic } from './shared/services/dispatcher/action-topic.enum';
import { MessageKeys } from './shared/services/dispatcher/message-keys';
import { MenuItemsService } from './shared/services/menu-items/menu-items.service';
import { MvdModalService } from './shared/services/modal/mvd-modal.service';
import { AppSettings } from './shared/settings/app-settings.model';
import { NewBusinessTransaction } from './transaction/models/new-business-transaction.model';
import { TransactionPrequalWindowComponent } from './transaction/prequal/transaction-prequal-modal.component';
import { TransactionActiveWindow } from './transaction/queue/modals/transaction-active-modal.component';
import { CheckinService } from './transaction/services/checkin.service';
import { AccessControlType } from './foundation/models/enums/access-control-type.enum';

declare global {
    interface Navigator {
        msSaveOrOpenBlob: (blobOrBase64: Blob | string, defaultName?: string) => void;
    }
}

@Component({
    selector: 'my-app',
    templateUrl: './app.html'
})
export class AppComponent extends BaseComponent {
    public currentMegaMenuIndex = -999;
    public menuItems: SecurityTransactionMenuItem[];
    public showMegaMenu = false;
    private businessTransactionNumberId: string;
    private menuItemSelected = false;
    private accessControlType: AccessControlType = new AccessControlType();
    public isAllowViewOmniSearchBar: boolean;

    constructor(
        public router: Router,
        public viewContainer: ViewContainerRef,
        private businessTransactionService: BusinessTransactionService,
        private checkInService: CheckinService,
        private redirectTransactionService: RedirectTransactionService,
        private menuItemsService: MenuItemsService,
        private idle: Idle,
        private modalService: MvdModalService,
    ) {
        super();
        setTheme('bs3');
        this.isAllowViewOmniSearchBar = false;
    }

    ngOnInit() {
        this.initIdleSetup();
        this.getMenuItems();

        this.auth.acsTransactionsUpdated.subscribe(acs => {
            this.isAllowViewOmniSearchBar = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, this.accessControlType.AllowViewOmniSearchBar.value);
        });
    }

    isItAnOutsideClickHandler(event) {
        if (event instanceof MouseEvent) {
            return;
        }

        if (event['IsItInside'] != null) {
            if (event['Id'].toLowerCase() == 'burgerButton'.toLowerCase()) {
                return;
            }
            if (event['IsItInside'] == true && this.menuItemSelected == false) {
                this.showMegaMenu = true;
            }
            if (event['IsItInside'] == false) {
                this.showMegaMenu = false;
                this.currentMegaMenuIndex = -999;
            }
        }
    }

    navigateToRoute(newRoute: any) {
        if (newRoute && Array.isArray(newRoute.commands) && newRoute.commands.length > 0) {
            this.router.navigate(newRoute.commands, newRoute.options);
        }
    }

    routeSelected() {
        this.showMegaMenu = false;
        this.menuItemSelected = true;
    }

    selectedService(id) {
        const data = { 'businessTransactionTypeId': id, 'megaMenu': true };
        this.modalService.open(TransactionPrequalWindowComponent, data).afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(result => this.startTransaction(result));

        this.routeSelected();
    }

    showMegaMenuToggle() {
        // Reset overall visibility
        this.showMegaMenu = !this.showMegaMenu;
        this.menuItemSelected = false;
    }

    startTransaction(validationRequestObject) {
        // TODO: Create businessTransactionValidationActionTypes enum
        if (validationRequestObject.businessTransactionValidationActionTypes) {
            if (!validationRequestObject.businessTransactionId || validationRequestObject.businessTransactionId == 0) {
                this.startNewTransaction(validationRequestObject);
            } else {
                this.validateActiveTransaction(validationRequestObject);
            }
        } else {
            if (!validationRequestObject.businessTransactionId || validationRequestObject.businessTransactionId == 0) {
                this.startNewTransaction(validationRequestObject);
            } else {
                this.validateActiveTransaction(validationRequestObject);
            }
        }
    }

    private beginService(validationRequestObject) {
        let businessTransaction: NewBusinessTransaction = new NewBusinessTransaction();
        businessTransaction.businessTransactionAreaId = validationRequestObject.businessTransactionAreaId;
        businessTransaction.businessTransactionTypeId = validationRequestObject.businessTransactionTypeId;
        businessTransaction.customerNumberId = validationRequestObject.itemKeys[0].itemKeyId;
        businessTransaction.agentId = validationRequestObject.itemKeys[0].itemKeyId;

        if (validationRequestObject.itemKeys.length > 1) {
            businessTransaction.businessTransactionParamId = validationRequestObject.itemKeys[1].itemKeyId;
        }
        let tranIdArray = [validationRequestObject.businessTransactionId];
        this.checkInService.checkin(tranIdArray, businessTransaction.agentId, []).subscribe(
            () => {
                this.businessTransactionService.assignBusinessTransactionsToUser(this.businessTransactionNumberId.toString(), 0, true)
                    .subscribe(
                        () => this.redirectTransactionService.redirectToTransaction(this.businessTransactionNumberId.toString(), this.viewContainer),
                        e => this.handleValidationResult(e)
                    );
            },
            e => this.handleValidationResult(e)
        );

    }

    private getMenuItems() {
        this.receiveMenuItemsUpdates();
        this.getMenuItemsFromStorage();
    }

    private getMenuItemsFromStorage() {
        if (!this.menuItems) {
            const stringifiedMenuItems = localStorage.getItem('menuItems');
            const storedMenuItems: SecurityTransactionMenuItem[] = JSON.parse(stringifiedMenuItems);
            if (storedMenuItems != null) {
                this.menuItemsService.sendData(storedMenuItems);
            }
        }
    }

    // Automatically Logs out inactive user
    private initIdleSetup() {
        const injector = appInjector();
        const appSettings = <AppSettings>injector.get(AppSettings);
        const logoutTime = appSettings.security.autoLogoutTime;
        // Set idle time
        this.idle.setIdle(logoutTime);
        // sets time to logout
        this.idle.setTimeout(logoutTime);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        // Start watching the inactivity
        this.idle.watch();

        // on Timeout
        this.idle.onTimeout.subscribe(() => {
            // Logout after the Idle Timeout
            if (this.auth.hasApplicationAccess) {
                console.log('Logging you out due IDLE Time - Token', this.auth.accessToken);
                this.auth.logOut();
            }
        });
    }

    private receiveMenuItemsUpdates() {
        this.menuItemsService.data$.subscribe(result => {
            const storedMenuItems = [];
            for (let i = 0; i < result.length; i++) {
                storedMenuItems.push(result[i]);
            }
            this.menuItems = storedMenuItems.filter(menuItem => menuItem.securityTransactionTypeMenuItemDisplayValue !== 'Reports');
            this.splitRouteString(this.menuItems);
        });
    }

    private refreshServiceSideBar(transactionId) {
        const confirmValues: Dictionary = new Dictionary([]);

        confirmValues.add(MessageKeys.BusinessTransactionNumber, transactionId);

        this.dispatcher.publish({ topic: ActionTopic.ServiceRefresh, values: confirmValues });

    }

    private splitRouteString(items) {
        for (let i = 0; i < items.length; i++) {
            for (let j = 0; j < items[i].children.length; j++) {
                if (items[i].children[j].children != null) {
                    for (let k = 0; k < items[i].children[j].children.length; k++) {
                        let routeString = items[i].children[j].children[k].routeString;
                        if (routeString) {
                            if (routeString === '/') {
                                items[i].children[j].children[k]['angularRouteString'] = routeString;
                            } else {
                                items[i].children[j].children[k]['angularRouteString'] = routeString.split('/');
                            }
                        }
                    }
                }
            }
        }
    }

    private startNewTransaction(validationRequestObject) {
        const self = this;
        const businessTranasction = new NewBusinessTransaction();

        businessTranasction.businessTransactionAreaId = validationRequestObject.businessTransactionAreaId;
        businessTranasction.businessTransactionTypeId = validationRequestObject.businessTransactionTypeId;

        businessTranasction.customerNumberId = validationRequestObject.itemKeys[0].itemKeyId;
        businessTranasction.agentId = validationRequestObject.itemKeys[0].itemKeyId;

        if (validationRequestObject.itemKeys.length > 1) {
            businessTranasction.businessTransactionParamId = validationRequestObject.itemKeys[1].itemKeyId;
        }

        const newBusinessTransactions = new Array<NewBusinessTransaction>();
        newBusinessTransactions.push(businessTranasction);

        this.checkInService.checkin(null, businessTranasction.customerNumberId, newBusinessTransactions).pipe(
            concatMap(r => {
                self.businessTransactionNumberId = r.businessTransactionIds[0].toString();
                return this.businessTransactionService.assignBusinessTransactionsToUser(self.businessTransactionNumberId, 0, true);
            }))
            .subscribe(
                result => {
                    this.redirectTransactionService.redirectToTransaction(self.businessTransactionNumberId.toString(), this.viewContainer);
                    this.refreshServiceSideBar(self.businessTransactionNumberId);
                }
                , err => {
                    this.handleValidationResult(err);
                });
    }

    private validateActiveTransaction(validationRequestObject) {
        if (validationRequestObject.businessTransactionId && validationRequestObject.details) {
            this.businessTransactionNumberId = validationRequestObject.businessTransactionId;
            if (validationRequestObject.details.wipStatusTypeId == 1) {
                const data = { businessTransactionUnitName: validationRequestObject.details.businessUnitName };
                const dialogRef = this.modalService.open(TransactionActiveWindow, data);

                dialogRef.afterClosed()
                    .pipe(filter(result => !!result))
                    .subscribe(result => this.beginService(validationRequestObject));
            } else {
                this.beginService(validationRequestObject);
            }
        }
    }
}
